import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, CardActions, Button, Chip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
  TextField, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import {
  PointOfSale as VentasIcon,
  ShoppingCart as ComprasIcon,
  TextSnippet as TxtIcon,
  TableChart as ExcelIcon,
  FindInPage as FindIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Catálogo de tipos de comunicación RVIE/RCE (01=propuesta sin modificar, 02=reemplaza
// propuesta) -- mismo catálogo estático que ya usan las pantallas SIRE de Facturación,
// se reutiliza vía ese endpoint para no duplicar la lista.
function useTiposCC() {
  const [tipos, setTipos] = useState({ rvie: [], rcie: [] });
  useEffect(() => {
    axios.get('/api/sire/tipos').then(r => setTipos(r.data || { rvie: [], rcie: [] })).catch(() => {});
  }, []);
  return tipos;
}

function fmtNum(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtFecha(d) {
  if (!d) return '';
  return String(d).substring(0, 10).split('-').reverse().join('/');
}

function descargarBlob(resp, fallback) {
  const disposition = resp.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const fname = match ? match[1] : fallback;
  const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fname;
  a.click();
  window.URL.revokeObjectURL(blobUrl);
}

function SelectorDialog({ open, onClose, tipo, empresaId, anio, mes, color, tiposCCList }) {
  const esVenta = tipo === 'ventas';
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [seleccion, setSeleccion] = useState(new Set());
  const [tipoCC, setTipoCC] = useState('02');
  const [anonimizar, setAnonimizar] = useState(false);
  const [error, setError] = useState('');
  const [descargando, setDescargando] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`/api/contabilidad/sire/${tipo}`, { params: { empresa_id: empresaId, anio, mes } });
      const data = Array.isArray(r.data) ? r.data : [];
      setRows(data);
      setSeleccion(new Set(data.map(d => d.id)));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [tipo, empresaId, anio, mes]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const toggle = (id) => {
    setSeleccion(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleTodos = () => {
    setSeleccion(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };

  const descargar = async (formato) => {
    setDescargando(formato); setError('');
    try {
      const params = {
        empresa_id: empresaId, anio, mes, tipo: tipoCC, formato,
        ids: [...seleccion].join(','),
      };
      if (esVenta) params.anonimizarAnulados = anonimizar ? '1' : '0';
      const resp = await axios.get(`/api/contabilidad/sire/${tipo}/descargar`, { params, responseType: 'blob' });
      descargarBlob(resp, `SIRE_${tipo}_${anio}${String(mes).padStart(2, '0')}.${formato === 'txt' ? 'txt' : 'xlsx'}`);
    } catch (e) {
      setError('Error al descargar: ' + (e.response?.data?.error || e.message));
    }
    setDescargando('');
  };

  const totalSeleccionado = rows.filter(r => seleccion.has(r.id)).reduce((s, r) => s + parseFloat(r.total || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: color, color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        {esVenta ? <VentasIcon /> : <ComprasIcon />}
        {esVenta ? 'SIRE Ventas — RVIE (Reemplazo de Propuesta)' : 'SIRE Compras — RCE (Reemplazo de Propuesta)'}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafbfc', pt: '20px !important' }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Elige qué documentos de tu Registro de {esVenta ? 'Ventas' : 'Compras'} (ya registrados en Contabilidad)
          declarar. Desmarca los que no correspondan — por ejemplo, comprobantes que SUNAT te atribuye pero que
          la empresa no acepta ni desea declarar.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
          <TextField select label="Tipo de comunicación" size="small" value={tipoCC}
            onChange={e => setTipoCC(e.target.value)} sx={{ width: '20em' }}>
            {(tiposCCList || []).map(t => <MenuItem key={t.cc} value={t.cc}>{t.label}</MenuItem>)}
          </TextField>
          {esVenta && (
            <FormControlLabel sx={{ m: 0 }}
              control={<Switch size="small" checked={anonimizar} onChange={e => setAnonimizar(e.target.checked)} />}
              label={<Typography variant="body2">Anonimizar anulados/rechazados</Typography>} />
          )}
          <Box sx={{ flex: 1 }} />
          <Chip label={`${seleccion.size} de ${rows.length} seleccionados`} color="primary" variant="outlined" />
          <Chip label={`Total: S/ ${fmtNum(totalSeleccionado)}`} sx={{ bgcolor: color, color: '#fff', fontWeight: 'bold' }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Alert severity="warning">No hay documentos registrados en Contabilidad para este período.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: color }}>
                  <TableCell padding="checkbox">
                    <Checkbox sx={{ color: '#fff' }} checked={seleccion.size === rows.length} indeterminate={seleccion.size > 0 && seleccion.size < rows.length} onChange={toggleTodos} />
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Fecha</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Serie-Número</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{esVenta ? 'Cliente' : 'Proveedor'}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">Total</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Moneda</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const anulado = (r.estado_cpe === 'ANULADO') || (r.estado === 'ANULADO');
                  return (
                    <TableRow key={r.id} hover selected={seleccion.has(r.id)} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={seleccion.has(r.id)} onChange={() => toggle(r.id)} />
                      </TableCell>
                      <TableCell>{fmtFecha(r.fecha_emision)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.tipo_comprobante || r.tipo_comprobante_proveedor}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.serie}-{r.numero}</TableCell>
                      <TableCell>{r.cliente_nombre || r.proveedor_nombre}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtNum(r.total)}</TableCell>
                      <TableCell>{r.moneda}</TableCell>
                      <TableCell>
                        {anulado
                          ? <Chip size="small" label="ANULADO" color="error" variant="outlined" />
                          : <Chip size="small" label="VIGENTE" color="success" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">Cerrar</Button>
        <Button variant="outlined" color="inherit" startIcon={descargando === 'excel' ? <CircularProgress size={16} /> : <ExcelIcon />}
          disabled={!!descargando || seleccion.size === 0} onClick={() => descargar('excel')}>
          Excel
        </Button>
        <Button variant="contained" sx={{ bgcolor: color }} startIcon={descargando === 'txt' ? <CircularProgress size={16} color="inherit" /> : <TxtIcon />}
          disabled={!!descargando || seleccion.size === 0} onClick={() => descargar('txt')}>
          Descargar TXT
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SireCard({ tipo, empresaId, anio, mes, color, colorBg, icon, titulo, descripcion, codigo }) {
  const [open, setOpen] = useState(false);
  const tiposCC = useTiposCC();
  return (
    <>
      <Card elevation={2} sx={{ border: `1px solid ${color}22`, borderTop: `4px solid ${color}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
            <Box sx={{ color, bgcolor: colorBg, borderRadius: 2, p: 1, display: 'flex', flexShrink: 0 }}>{icon}</Box>
            <Box>
              <Chip label={codigo} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color }}>{titulo}</Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">{descripcion}</Typography>
        </CardContent>
        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button fullWidth variant="contained" sx={{ bgcolor: color }} startIcon={<FindIcon />}
            disabled={!empresaId || !anio || !mes} onClick={() => setOpen(true)}>
            Seleccionar y Declarar
          </Button>
        </CardActions>
      </Card>
      <SelectorDialog open={open} onClose={() => setOpen(false)} tipo={tipo}
        empresaId={empresaId} anio={anio} mes={mes} color={color}
        tiposCCList={tipo === 'ventas' ? tiposCC.rvie : tiposCC.rcie} />
    </>
  );
}

export default function SireReemplazoContabilidad({ empresaId, anio, mes }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: '#00695c' }}>
        SIRE — REEMPLAZO DE PROPUESTA (DESDE CONTABILIDAD)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Genera el archivo que declara el Registro de Ventas (RVIE) o Compras (RCE) a partir de lo que ya
        registraste en Contabilidad — útil cuando la empresa no usa Facturación en este sistema, o cuando
        quiere declarar un subconjunto distinto al que SUNAT propuso.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 300 }}>
          <SireCard tipo="ventas" empresaId={empresaId} anio={anio} mes={mes}
            color="#00695c" colorBg="#E0F2F1" icon={<VentasIcon sx={{ fontSize: 32 }} />}
            codigo="RVIE" titulo="SIRE Ventas (Reemplazo)"
            descripcion="Genera el Anexo 3 a partir de tu Registro de Ventas en Contabilidad." />
        </Box>
        <Box sx={{ flex: '1 1 320px', minWidth: 300 }}>
          <SireCard tipo="compras" empresaId={empresaId} anio={anio} mes={mes}
            color="#00838f" colorBg="#E0F7FA" icon={<ComprasIcon sx={{ fontSize: 32 }} />}
            codigo="RCE" titulo="SIRE Compras (Reemplazo)"
            descripcion="Genera el Anexo 11 a partir de tu Registro de Compras en Contabilidad." />
        </Box>
      </Box>
    </Box>
  );
}
